import { formatISO, set } from 'date-fns'
import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { kuiperClassNames } from '@opallabs/kuiper'
import { Button, DotLoader, TextArea, toast } from '@opallabs/lapidary'
import { AllowedEvents, analyticsContext } from '@opallabs/quanta'
import CardContext from '../cardContext'
import type { PartialMoment } from '../momentTypes'
import {
  ServiceOption,
  useAccountToServiceMap,
  useSystemServiceOptions,
} from '../queries/useSystemServices'
import { WeekViewSortType } from '../types'
import { useContentMap } from '../useMomentMap'
import { WeekViewGlobalContext } from '../weekViewGlobalContext'
import { AttachMomentPicker } from './AttachMomentPicker'
import { ChannelPicker } from './ChannelPicker'
import { contentCreatorStyles } from './styles'

type ContentQuickCreateProps = {
  date: Date
  /**
   * Sort dimension currently active. Drives smart pre-selection + whether
   * the category value should populate day_segment on the new moment.
   */
  sortType: WeekViewSortType
  /**
   * The enclosing category's identifier. What this means depends on sortType:
   *   - Time: 'morning' / 'afternoon' / 'evening' / 'night' / null
   *   - Channel: a service system_name (e.g. 'facebook')
   *   - Account: an account name
   *   - LabelSet: a label name within the selected label set
   */
  categoryId?: string | null
  /** Human label for the category — shown or used as moment seed data. */
  categoryTitle?: string
  onExitForm: () => void
}

const MAX_LENGTH = 100

// Channel pre-selection based on the sort dimension. The Channel branch
// has to fall back to case-insensitive systemName and then display name
// because `selectCurrentWeekChannels` (column source) and
// `useSystemServiceOptions` (picker source) can disagree on shape.
const resolvePreselectedService = ({
  services,
  sortType,
  categoryId,
  categoryTitle,
  accountToService,
}: {
  services: ServiceOption[]
  sortType: WeekViewSortType
  categoryId: string | null | undefined
  categoryTitle: string | undefined
  accountToService: Record<string, string>
}): string | null => {
  if (sortType === WeekViewSortType.Channel && categoryId) {
    const target = categoryId.toLowerCase()
    const byChannel =
      services.find((s) => s.systemName === categoryId) ??
      services.find((s) => s.systemName?.toLowerCase() === target) ??
      (categoryTitle
        ? services.find((s) => s.name === categoryTitle)
        : undefined)
    if (byChannel) return byChannel.id
  }
  if (sortType === WeekViewSortType.Account && categoryId) {
    return accountToService[categoryId] ?? null
  }
  if (services.length === 1) return services[0].id
  return null
}

/**
 * Inline content creator. Mirrors `CreateMomentCard`'s structure — TextArea
 * for the title, channel picker in the footer. Uses the shared
 * `CardContext.createMoment` path (same one the moment creator uses), which
 * handles Redux dispatch + content creation + `loadUpdatedJsonApiEntities`,
 * so the new content shows up in the week view immediately without a reload.
 */
export const ContentQuickCreate: React.FC<ContentQuickCreateProps> = ({
  date,
  sortType,
  categoryId,
  categoryTitle,
  onExitForm,
}) => {
  const [title, setTitle] = useState('')
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
    null
  )
  const [selectedMoment, setSelectedMoment] = useState<PartialMoment | null>(
    null
  )
  const [error, setError] = useState(false)
  const [showLoader, setShowLoader] = useState(false)
  const textArea = useRef<HTMLTextAreaElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const pickerOpen = useRef(false)
  const titleRef = useRef('')
  const { currentBrandId, setDisableActiveColumn } = useContext(
    WeekViewGlobalContext
  )
  const { createMoment, createContentForMoment } = useContext(CardContext) ?? {}
  const { moments } = useContentMap()
  const { track } = useContext(analyticsContext)
  const brandId = currentBrandId?.toString() ?? ''
  const { data: services = [] } = useSystemServiceOptions(brandId) as {
    data?: ServiceOption[]
  }
  const { data: accountToService = {} } = useAccountToServiceMap(brandId)

  const errorMessage = `Title cannot be longer than ${MAX_LENGTH} characters.`

  useEffect(() => {
    if (selectedServiceId) return
    const resolved = resolvePreselectedService({
      services,
      sortType,
      categoryId,
      categoryTitle,
      accountToService,
    })
    if (resolved) setSelectedServiceId(resolved)
  }, [
    services,
    selectedServiceId,
    sortType,
    categoryId,
    categoryTitle,
    accountToService,
  ])

  // Awaits the full create lifecycle so the loader stays in place until
  // the real card lands — mirrors CreateMomentCard's UX. The loader state
  // doubles as a duplicate-click guard: subsequent submits are inert
  // while the mutation is in flight.
  const submit = async (service: ServiceOption | undefined) => {
    if (showLoader) return
    if (!title || error || !service || !createMoment) return
    setShowLoader(true)

    // Moments are day-granular — a time-of-day on their `scheduled_at`
    // fails server-side schedule validation. The day is all the moment
    // needs; when grouped by Time, the hour belongs on the content,
    // which we anchor via a follow-up schedule patch (the content gets
    // its `scheduled_at` set during/after creation, not on the moment).
    const momentScheduledAt = formatISO(date, { representation: 'date' })
    let contentScheduledAt
    if (sortType === WeekViewSortType.Time && categoryId) {
      const hour = parseInt(categoryId, 10)
      if (!Number.isNaN(hour)) {
        contentScheduledAt = formatISO(
          set(date, { hours: hour, minutes: 0, seconds: 0, milliseconds: 0 })
        )
      }
    }

    if (selectedMoment && createContentForMoment) {
      track(AllowedEvents.submit, { type: 'content', action: 'attach' })
      try {
        await createContentForMoment({
          momentId: selectedMoment.id,
          service,
          name: title,
          scheduled_at: contentScheduledAt ?? momentScheduledAt,
        })
      } catch {
        toast('Could not attach content to that moment. Please try again.', {
          type: 'error',
        })
      } finally {
        onExitForm()
      }
      return
    }

    track(AllowedEvents.submit, { type: 'content' })
    try {
      await createMoment(
        { title, scheduled_at: momentScheduledAt },
        [service],
        {
          applyNameToContent: true,
          contentScheduledAt,
        }
      )
    } catch {
      toast('Could not create content. Please try again.', { type: 'error' })
    } finally {
      onExitForm()
    }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (!title || error) return
      const selectedService = services.find((s) => s.id === selectedServiceId)
      if (selectedService) submit(selectedService)
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      onExitForm()
    }
  }

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    if (value.length > MAX_LENGTH && !error) setError(true)
    else if (value.length <= MAX_LENGTH && error) setError(false)
    setTitle(value)
    titleRef.current = value
  }

  const onChannelSelect = (service: ServiceOption) => {
    setSelectedServiceId(service.id)
  }

  const onClickCreate = () => {
    const selectedService = services.find((s) => s.id === selectedServiceId)
    if (!selectedService) return
    submit(selectedService)
  }

  // Picker is staging-only — clicking a moment toggles it as the
  // attach target. Actual creation only fires when the Create button
  // is clicked.
  const onSelectMoment = (moment: PartialMoment) => {
    setSelectedMoment((prev) => (prev?.id === moment.id ? null : moment))
  }

  const onPickerOpen = () => {
    pickerOpen.current = true
    setDisableActiveColumn(true)
  }

  const onPickerClose = () => {
    pickerOpen.current = false
    setDisableActiveColumn(false)
    setTimeout(() => textArea.current?.focus(), 0)
  }

  useEffect(() => {
    textArea.current?.focus()
  }, [])

  // Dismiss on real outside-click when empty. `capture: true` fires
  // before Radix's own outside-click handler flips `pickerOpen` to
  // false — otherwise closing a picker would also close the card.
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (!target) return
      if (cardRef.current?.contains(target)) return
      if (pickerOpen.current) return
      if (!titleRef.current) onExitForm()
    }
    document.addEventListener('mousedown', onMouseDown, true)
    return () => document.removeEventListener('mousedown', onMouseDown, true)
  }, [onExitForm])

  return (
    <div css={contentCreatorStyles.cardBody} ref={cardRef}>
      <div style={{ position: 'relative' }}>
        {showLoader ? (
          <DotLoader height={44} />
        ) : (
          <TextArea
            ref={textArea}
            error={error ? errorMessage : ''}
            value={title}
            name="Create content"
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder="Add Name..."
            errorStyleOverrides={{ marginBlockEnd: 4 }}
            styleOverrides={contentCreatorStyles.textarea}
          />
        )}
      </div>
      <div
        css={contentCreatorStyles.cardFooter}
        className={kuiperClassNames('emptyMomentCard-footer')}
      >
        <div css={contentCreatorStyles.leftActions}>
          <ChannelPicker
            services={services}
            selectedServiceId={selectedServiceId}
            onSelect={onChannelSelect}
            disabled={error}
            onOpen={onPickerOpen}
            onClose={onPickerClose}
          />
          <AttachMomentPicker
            moments={moments}
            date={date}
            selectedMomentId={selectedMoment?.id ?? null}
            onSelect={onSelectMoment}
            onClear={() => setSelectedMoment(null)}
            onOpen={onPickerOpen}
            onClose={onPickerClose}
          />
        </div>
        <Button
          buttonText="Create"
          variant="primary"
          disabled={!title || error || !selectedServiceId}
          name="content-create-submit"
          onClick={onClickCreate}
          overrideStyles={contentCreatorStyles.createButton}
        />
      </div>
    </div>
  )
}
